/**
 * Strip backslash escapes from markdown that aren't load-bearing under
 * GFM parsing: greedily drop all candidates, re-parse, and compare against
 * the source mdast. If the trees match, the escapes weren't needed; if
 * not, bisect to find the minimal set that must stay.
 *
 * Lets the real GFM parser decide what's load-bearing rather than
 * hand-coding flanking rules per character.
 */

import { fromMarkdown } from 'mdast-util-from-markdown';
import { gfmTableFromMarkdown } from 'mdast-util-gfm-table';
import { gfmTaskListItemFromMarkdown } from 'mdast-util-gfm-task-list-item';
import { gfmStrikethroughFromMarkdown } from 'mdast-util-gfm-strikethrough';
import { gfmTable } from 'micromark-extension-gfm-table';
import { gfmTaskListItem } from 'micromark-extension-gfm-task-list-item';
import { gfmStrikethrough } from 'micromark-extension-gfm-strikethrough';
import type { Root } from 'mdast';

/*
 * Match the GFM extensions used by the serializer. Omits
 * gfm-autolink-literal: the serializer doesn't insert escapes to prevent
 * literal autolinking, so including it would cause unrelated verify
 * failures on any doc that mentions a URL.
 */
const PARSE_EXTENSIONS = [gfmTable(), gfmTaskListItem(), gfmStrikethrough()];
const PARSE_MDAST_EXTENSIONS = [
	gfmTableFromMarkdown(),
	gfmTaskListItemFromMarkdown(),
	gfmStrikethroughFromMarkdown(),
];

// ASCII-punctuation escape set per CommonMark + GFM.
const ESCAPABLE = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/u;

/*
 * Index of each `\X` where X is escapable. Consecutive escape pairs
 * (e.g. `\\\\` for two literal backslashes) are reported as separate
 * candidates; the bisect step decides which can be removed.
 */
function findEscapePositions(md: string): number[] {
	const positions: number[] = [];
	for (let i = 0; i < md.length - 1; i++) {
		if (md[i] === '\\' && ESCAPABLE.test(md[i + 1])) {
			positions.push(i);
			// Skip the escaped char so it isn't misread as another escape.
			i += 1;
		}
	}
	return positions;
}

function stripAt(md: string, drop: ReadonlySet<number>): string {
	if (drop.size === 0) {
		return md;
	}
	const parts: string[] = [];
	for (let i = 0; i < md.length; i++) {
		if (!drop.has(i)) {
			parts.push(md[i]);
		}
	}
	return parts.join('');
}

/*
 * Strip `position` so trees from different sources compare structurally.
 * Emit keys in sorted order so the `JSON.stringify`-based comparison
 * doesn't get tripped up if the PM handlers and `fromMarkdown` happen to
 * construct a given node type with different key orderings.
 */
function normalise(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(normalise);
	}
	if (value && typeof value === 'object') {
		const source = value as Record<string, unknown>;
		const out: Record<string, unknown> = {};
		for (const k of Object.keys(source).sort()) {
			if (k === 'position') {
				continue;
			}
			out[k] = normalise(source[k]);
		}
		return out;
	}
	return value;
}

function treesEqual(a: Root, b: Root): boolean {
	return JSON.stringify(normalise(a)) === JSON.stringify(normalise(b));
}

function parse(md: string): Root {
	return fromMarkdown(md, {
		extensions: PARSE_EXTENSIONS,
		mdastExtensions: PARSE_MDAST_EXTENSIONS,
	});
}

/**
 * Identify which escapes in `positions` must be kept for `md` to round-trip
 * back to `target`, accumulating them into `kept`.
 *
 * Try removing every position at once; if the result still round-trips,
 * none of these escapes are needed. Otherwise split the candidate set in
 * half and recurse on each half independently — a 1-element set whose
 * trial fails is necessarily load-bearing.
 *
 * This is a delta-debugging-style search (ddmin family: Zeller, "Yesterday,
 * My Program Worked. Today, It Does Not. Why?"). The result is 1-minimal:
 * no single position in `kept` can be removed while preserving round-trip.
 * It is NOT necessarily a globally minimum subset — the defensive verify
 * in `reduceUnnecessaryEscaping` catches the rare case where individual
 * removals don't commute.
 */
function findMinimumEscapeSubset(
	md: string,
	positions: number[],
	target: Root,
	kept: Set<number>,
): void {
	if (positions.length === 0) {
		return;
	}
	const trial = stripAt(md, new Set(positions));
	if (treesEqual(parse(trial), target)) {
		return;
	}
	if (positions.length === 1) {
		kept.add(positions[0]);
		return;
	}
	const mid = Math.floor(positions.length / 2);
	findMinimumEscapeSubset(md, positions.slice(0, mid), target, kept);
	findMinimumEscapeSubset(md, positions.slice(mid), target, kept);
}

/**
 * `target` is the mdast we serialized from — the ground truth. We can't
 * trust the markdown string itself since over-escaping is the thing
 * we're fixing.
 */
export function reduceUnnecessaryEscaping(md: string, target: Root): string {
	const positions = findEscapePositions(md);
	if (positions.length === 0) {
		return md;
	}

	// Fast path: drop everything at once.
	const allDropped = stripAt(md, new Set(positions));
	if (treesEqual(parse(allDropped), target)) {
		return allDropped;
	}

	// Single escape that the fast path already proved is load-bearing.
	if (positions.length === 1) {
		return md;
	}

	/*
	 * Slow path: enter the recursion at the first split. The fast path
	 * already proved removing all positions fails, so re-running that
	 * same trial at the top of findMinimumEscapeSubset would be wasted
	 * work.
	 */
	const kept = new Set<number>();
	const mid = Math.floor(positions.length / 2);
	findMinimumEscapeSubset(md, positions.slice(0, mid), target, kept);
	findMinimumEscapeSubset(md, positions.slice(mid), target, kept);
	const candidate = stripAt(md, new Set(positions.filter((p) => !kept.has(p))));

	/*
	 * The search assumes individual-escape removals commute. If that ever
	 * fails for some input, fall back to the always-safe original rather
	 * than emit something that doesn't round-trip.
	 */
	if (!treesEqual(parse(candidate), target)) {
		return md;
	}
	return candidate;
}
